<template>
  <b-container fluid>
    <admin-breadcrumb name="CTS Schedule Upload"/>
    <b-row>
      <b-col cols="12" lg="8">
        <admin-panel
            title="Upload CTS Weekly Schedule"
            sub-title="Upload the current CTS weekly schedule workbook. The in prog sheet will replace the existing weekly route assignments."
            with-footer
            :second-body="!!importResult"
        >
          <b-alert show variant="warning">
            Uploading a schedule replaces every row in the CTS weekly route assignments table.
          </b-alert>

          <b-alert show variant="info">
            <strong>Same-day pickups:</strong> Set column A to <code>Yes</code>, <code>Y</code>,
            <code>True</code>, or <code>1</code> for routes that permit same-day scheduling.
            Same-day routes are available until 4:00 PM Mountain Time. Blank or unrecognized
            values disable same-day scheduling for that route.
          </b-alert>

          <b-form @submit.prevent="submitSchedule">
            <b-form-group
                label-for="cts-weekly-schedule-file"
                :invalid-feedback="validation.file"
            >
              <template #label>
                Weekly Schedule Workbook <span class="required-asterisk">*</span>
              </template>
              <b-form-file
                  id="cts-weekly-schedule-file"
                  v-model="form.file"
                  :state="fieldState('file')"
                  placeholder="Choose the CTS schedule workbook or drop it here"
                  drop-placeholder="Drop workbook here"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </b-form-group>
          </b-form>

          <template v-slot:footer>
            <b-btn
                variant="primary"
                :disabled="formState.processing || !form.file"
                @click.prevent="submitSchedule"
            >
              Upload Schedule
              <b-spinner v-if="formState.processing" class="ml-1" small/>
            </b-btn>
          </template>

          <template v-slot:second-body>
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h5 class="mb-1">Latest Import</h5>
                <p class="text-muted mb-0">
                  {{ importResult.source_sheet_name }} imported successfully.
                </p>
              </div>
              <b-badge variant="success">{{ importResult.imported }} rows</b-badge>
            </div>

            <b-row>
              <b-col sm="6" md="3">
                <div class="summary-label">Date Range</div>
                <div class="summary-value">{{ dateRange }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Active Routes</div>
                <div class="summary-value">{{ importResult.active }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Inactive Routes</div>
                <div class="summary-value">{{ importResult.inactive }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Same-Day Routes</div>
                <div class="summary-value">
                  <b-badge :variant="importResult.same_day_enabled ? 'success' : 'secondary'">
                    {{ importResult.same_day_enabled || 0 }} enabled
                  </b-badge>
                </div>
              </b-col>
            </b-row>
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import {API_ROUTES} from "../api";

export default {
  name: "AdminCtsScheduleUpload",
  components: {AdminPanel, AdminBreadcrumb},
  data() {
    return {
      form: {
        file: null
      },
      formState: {
        processing: false
      },
      validation: {},
      importResult: null
    };
  },
  computed: {
    dateRange() {
      if (!this.importResult) {
        return '';
      }

      if (this.importResult.service_date_from === this.importResult.service_date_to) {
        return this.importResult.service_date_from;
      }

      return `${this.importResult.service_date_from} to ${this.importResult.service_date_to}`;
    }
  },
  methods: {
    fieldState(fieldName) {
      if (typeof this.validation[fieldName] === 'undefined') {
        return null;
      }

      return false;
    },
    submitSchedule() {
      if (!this.form.file) {
        this.validation = {
          file: 'Please choose a CTS schedule workbook.'
        };

        return;
      }

      const formData = new FormData();
      formData.append('file', this.form.file, this.form.file.name);

      this.formState.processing = true;
      this.validation = {};

      this.$api.post(API_ROUTES.ADMIN_CTS_WEEKLY_SCHEDULE_IMPORT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(({data}) => {
        this.importResult = data.summary;
        this.form.file = null;
        this.$bvToast.toast('CTS weekly schedule imported successfully.', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response}) => {
        const data = response?.data || {};
        const validationErrors = {};

        if (typeof data.errors !== 'undefined') {
          Object.keys(data.errors).forEach((fieldName) => {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          });
        }

        this.validation = validationErrors;
        this.$bvToast.toast(data.message ?? 'Failed to import CTS weekly schedule.', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  }
}
</script>

<style scoped>
.required-asterisk {
  color: #dc3545;
}

.summary-label {
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: uppercase;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
</style>
